import React from 'react';
import { SPACE, THEME_CONFIG, THEME_TOKENS, getConfigValue, getTokenValue } from '@amtrav/theme-system';
import { DynamicThemeProvider, getThemeProp, MatchMedia } from '@amtrav/themes';
import { animated } from 'react-spring';
import { Popover, Avatar } from '@amtrav/view-components';
import { IconCaretDown } from '@amtrav/icons';
import { Flex, Box } from '@rebass/grid';
import { hot } from 'react-hot-loader/root';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import MenuPopover from './MenuPopover';
import useBoop from '../../hooks/useBoop';
import useGlobalUserData from './useGlobalUserData';

const CompanyLogoRegion = styled(Flex)`
  padding: ${SPACE.size0} ${SPACE.size16};
  filter: ${({ fullColor }) => (fullColor ? 'none' : 'saturate(0) brightness(0.85) contrast(1.2)')};

  img {
    max-width: 94px;
    max-height: 30px;
  }
`;

const UserMenuTarget = styled(Flex)`
  border: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
  border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)};
  background-color: ${getTokenValue(THEME_TOKENS.base.color.neutral0)};
  padding: ${SPACE.size8} ${SPACE.size12} ${SPACE.size8} ${SPACE.size0};
  display: inline-flex;
  min-height: 50px;

  &:hover {
    cursor: pointer;
    border-color: transparent;
    box-shadow: ${getTokenValue(THEME_TOKENS.base.shadow.raised)};
  }
`;

const AvatarLink = styled.a`
  text-decoration: none;
`;

const CaretContainer = styled(Flex)`
  color: ${getThemeProp('global.link--color')};
`;

const CompanyName = styled(Box)`
  font-size: 16px;
  font-weight: 600;
  color: ${getThemeProp('global.link--color')};
`;

const ImportantLink = styled.a`
  color: ${getThemeProp('global.danger--color')};
  font-weight: 600;
  font-size: 16px;

  &:active,
  &:hover {
    color: ${getThemeProp('global.danger--color')};
  }
`;

const UserMenu = ({ themeName, getLogonProfile }) => {
  const [style, trigger] = useBoop({ y: 2 });
  const [, userData] = useGlobalUserData(getLogonProfile);

  if (userData?.viewSettings && !userData?.viewSettings?.navMyProfile) {
    return (
      <DynamicThemeProvider themeName={themeName}>
        <ImportantLink href="/logout">Logout</ImportantLink>
      </DynamicThemeProvider>
    );
  }

  // only show the company logo if an image is available and not using one of our white label themes
  const showCompanyLogo = userData?.companyLogoUrl;

  return (
    <DynamicThemeProvider themeName={themeName}>
      <Flex alignItems="center">
        <MatchMedia.MediumAndLess>
          <AvatarLink href="/profile/">
            <Avatar size="40px" bordered imgUrl={userData?.profileImageUrl} initials={userData?.initials} />
          </AvatarLink>
        </MatchMedia.MediumAndLess>
        <MatchMedia.Large>
          <Popover
            // open
            maxWidth="unset"
            placement="bottom"
            withPadding={false}
            content={
              <MenuPopover
                showProfileLink={userData?.viewSettings?.navMyProfile}
                navPolicyDoc={userData?.viewSettings?.navPolicyDoc}
                userRole={userData?.userRole}
                email={userData?.email}
                userName={userData ? `${userData?.firstName} ${userData?.lastName}` : ''}
                companyName={showCompanyLogo ? userData?.companyName : ''}
              />
            }>
            <UserMenuTarget onMouseEnter={trigger} alignItems="center">
              {!showCompanyLogo ? (
                <Flex flexDirection="column" pr={SPACE.size12} pl={SPACE.size16}>
                  <CompanyName>{userData?.companyName}</CompanyName>
                </Flex>
              ) : null}

              {showCompanyLogo ? (
                <CompanyLogoRegion fullColor={!getConfigValue(THEME_CONFIG.base.flag.isFlagshipSite)}>
                  <img src={userData.companyLogoUrl} alt={`${userData?.companyName} Logo`} />
                </CompanyLogoRegion>
              ) : null}
              <Avatar size="32px" imgUrl={userData?.profileImageUrl} initials={userData?.initials} />
              <animated.span style={style}>
                <CaretContainer pl={2}>
                  <IconCaretDown />
                </CaretContainer>
              </animated.span>
            </UserMenuTarget>
          </Popover>
        </MatchMedia.Large>
      </Flex>
    </DynamicThemeProvider>
  );
};

UserMenu.propTypes = {
  themeName: PropTypes.string.isRequired,
  getLogonProfile: PropTypes.func.isRequired,
};

export default process.env.USE_HMR ? hot(UserMenu) : UserMenu;
